import { Injectable } from '@angular/core';

import { Auth } from '@aws-amplify/auth';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { Store } from '@ngrx/store';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { BehaviorSubject, from, map, Observable } from 'rxjs';

import { SiteContextUpdateRequest } from '@app/api/marketplace/models';
import { ImpersonationActions } from '@app/easylot-admin/modules/impersonation/store/impersonation.slice';
import { MerchantAccountActions } from '@app/merchant-account/store/merchant-account.slice';
import { AuthenticationActions } from '@authentication/store/authentication.slice';
import { ErrorActions } from '@error/store/error.slice';
import { MerchantActions } from '@merchant/store/merchant.slice';
import { PackagingActions } from '@packaging/store/packaging.slice';
import { ProductActions } from '@products/store/products.slice';
import { StatementActions } from '@products/store/statement.slice';
import { ProductSearchActions } from '@search/store/search.slice';
import { UTCDateToLocale } from '@shared/helpers/date-converter';

import { UserDetails } from '../interfaces/user';

@Injectable({ providedIn: 'root' })
export class UserService {
  currentSession = new BehaviorSubject<any>(null);
  currentUser = new BehaviorSubject<UserDetails | null>(null);
  currentUserRoles = new BehaviorSubject<string[] | null>(null);

  constructor(
    private readonly authenticator: AuthenticatorService,
    private readonly store: Store
  ) {}

  getCurrentSession(): Observable<CognitoUserSession> {
    return from(this.getCurrentSessionAsync());
  }

  getCurrentUserInfo(): Observable<any> {
    return from(Auth.currentUserInfo());
  }

  getUser(): Observable<UserDetails> {
    return from(this.getUserAsync());
  }

  getRoles(): Observable<string[]> {
    return from(this.getRolesAsync());
  }

  hasAnyRole(requiredRoles: string[]): Observable<boolean> {
    return this.getRoles().pipe(
      map((roles) => requiredRoles.some((role) => roles.includes(role)))
    );
  }

  hasAllRoles(requiredRoles: string[]): Observable<boolean> {
    return this.getRoles().pipe(
      map((roles) => requiredRoles.every((role) => roles.includes(role)))
    );
  }

  async getCurrentSessionAsync(): Promise<CognitoUserSession> {
    return await Auth.currentSession();
  }

  async getUserAsync(): Promise<UserDetails> {
    const roles = await this.getRolesAsync();
    const user = await Auth.currentUserInfo();
    return {
      email: user?.attributes?.email,
      familyName: user?.attributes?.family_name,
      givenName: user?.attributes?.given_name,
      roles: user?.attributes?.['custom:impRoles']
        ? user?.attributes?.['custom:impRoles'].split(',')
        : roles,
      impersonated: !!user?.attributes?.['custom:impMerchantId'],
      impersonationTTL: UTCDateToLocale(user?.attributes?.['custom:impTTL']),
    };
  }

  private async getRolesAsync(): Promise<string[]> {
    const session = await Auth.currentSession();
    const payload = session.getIdToken().payload;
    const impRoles = payload['custom:impRoles'];
    const groups = payload['cognito:groups'];
    // Defensive normalization (see EAS-2036 review):
    //  - custom:impRoles === '' would split to [''] (truthy → ?? would
    //    not fall through). Treat empty string as "no override".
    //  - cognito:groups is normally string[], but a malformed token
    //    could deliver a string; that would cause substring matching
    //    in hasAnyRole / hasAllRoles. Coerce non-array to null.
    //  - Final ?? [] guards the no-claims case so hasAnyRole returns
    //    false instead of throwing on `undefined.includes(...)`.
    return (
      (typeof impRoles === 'string' && impRoles.length > 0
        ? impRoles.split(',')
        : null) ??
      (Array.isArray(groups) ? groups : null) ??
      []
    );
  }

  public resetStores() {
    // reset stores
    this.store.dispatch(MerchantAccountActions.reset());
    this.store.dispatch(ProductActions.reset());
    this.store.dispatch(MerchantActions.reset());
    this.store.dispatch(PackagingActions.reset());
    this.store.dispatch(ErrorActions.resetErrors());
    this.store.dispatch(AuthenticationActions.resetState());
    this.store.dispatch(StatementActions.reset());
    this.store.dispatch(ProductSearchActions.reset());
  }

  private signOutAndReset() {
    this.authenticator.signOut();

    this.resetStores();

    this.currentUser.next(null);
    this.currentUserRoles.next(null);
  }

  public logout() {
    this.getUserAsync().then((user) => {
      if (user.impersonated) {
        this.store.dispatch(
          ImpersonationActions.removeImpersonation.trigger({ noRefresh: true })
        );
        this.signOutAndReset();
      } else {
        this.signOutAndReset();
      }
    });
  }

  switchContext(ctx: SiteContextUpdateRequest) {
    this.store.dispatch(
      AuthenticationActions.contextSwitch.trigger({ data: ctx })
    );
  }
  refresh() {
    this.store.dispatch(AuthenticationActions.refreshToken.trigger());
  }
}
